import { useState, useEffect } from 'react';
import { formatTimestamp, formatDate } from '../utils/date';
import {
  Typography,
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  Tabs,
  Alert,
  Divider,
  App,
  Spin,
  Table,
  Tag,
  Modal,
  Select,
  Popconfirm,
  DatePicker,
} from 'antd';
import {
  MailOutlined,
  ApiOutlined,
  SaveOutlined,
  ReloadOutlined,
  UserOutlined,
  LockOutlined,
  SwapOutlined,
  TeamOutlined,
  KeyOutlined,
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  getSettings,
  updateSmtpSettings,
  changePassword,
  type AppSettings,
  type SmtpSettings,
  type PasswordChangeRequest,
} from '../api/settings';
import { getUsers, createUser, updateUser, deactivateUser, resetUserPassword, type User } from '../api/users';
import { getApiKeys, createApiKey, revokeApiKey, type ApiKey } from '../api/apiKeys';
import { AliasesTab } from '../components/settings/AliasesTab';
import { ProvidersTab } from '../components/settings/ProvidersTab';
import { useAuth } from '../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

// ============================================================================
// Users Tab Component
// ============================================================================
function UsersTab() {
  const { message } = App.useApp();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [createForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const auth = useAuth();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      setUsers(await getUsers());
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (values: { username: string; password: string; role: string }) => {
    setSaving(true);
    try {
      await createUser({ username: values.username, password: values.password, role: values.role as 'admin' | 'readonly' });
      message.success('User created');
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchUsers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (user: User, newRole: 'admin' | 'readonly') => {
    try {
      await updateUser(user.id, { role: newRole });
      message.success(`${user.username} updated to ${newRole}`);
      fetchUsers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeactivate = async (user: User) => {
    try {
      await deactivateUser(user.id);
      message.success(`${user.username} deactivated`);
      fetchUsers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (user: User) => {
    try {
      await updateUser(user.id, { is_active: true });
      message.success(`${user.username} reactivated`);
      fetchUsers();
    } catch {
      message.error('Failed to reactivate user');
    }
  };

  const handleResetPassword = async (values: { new_password: string }) => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await resetUserPassword(selectedUser.id, values.new_password);
      message.success(`Password reset for ${selectedUser.username}`);
      setResetModalOpen(false);
      resetForm.resetFields();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Username', dataIndex: 'username', key: 'username' },
    {
      title: 'Role', dataIndex: 'role', key: 'role',
      render: (role: string, record: User) => (
        <Select
          value={role}
          size="small"
          style={{ width: 120 }}
          onChange={(v) => handleRoleChange(record, v as 'admin' | 'readonly')}
          options={[
            { value: 'admin', label: 'Admin' },
            { value: 'readonly', label: 'Read-only' },
          ]}
        />
      ),
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'status',
      render: (active: boolean) => active
        ? <Tag color="green">Active</Tag>
        : <Tag color="red">Inactive</Tag>,
    },
    {
      title: 'Last Login', dataIndex: 'last_login', key: 'last_login',
      render: (v: string | null) => v ? formatTimestamp(v) : 'Never',
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: User) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() => { setSelectedUser(record); setResetModalOpen(true); }}
          >
            Reset Password
          </Button>
          {record.is_active ? (
            <Popconfirm
              title={`Deactivate ${record.username}?`}
              onConfirm={() => handleDeactivate(record)}
              disabled={record.username === auth.token && false} // Can't deactivate self (handled server-side)
            >
              <Button size="small" danger>Deactivate</Button>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => handleReactivate(record)}>Reactivate</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Manage user accounts and roles. Admin users have full access. Read-only users can search and browse products.
        </Paragraph>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
          Add User
        </Button>
      </div>

      <Table columns={columns} dataSource={users} rowKey="id" loading={loading} size="small" />

      <Modal
        title="Create User"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields(); }}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item
            name="password" label="Password"
            rules={[{ required: true }, { min: 8, message: 'Min 8 characters' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item name="role" label="Role" initialValue="readonly">
            <Select options={[
              { value: 'admin', label: 'Admin — Full access' },
              { value: 'readonly', label: 'Read-only — Browse products only' },
            ]} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} block>Create User</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Reset Password: ${selectedUser?.username}`}
        open={resetModalOpen}
        onCancel={() => { setResetModalOpen(false); resetForm.resetFields(); }}
        footer={null}
      >
        <Alert type="info" message="User will be required to change password on next login." style={{ marginBottom: 16 }} />
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item
            name="new_password" label="New Password"
            rules={[{ required: true }, { min: 8, message: 'Min 8 characters' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} block>Reset Password</Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

// ============================================================================
// API Keys Tab Component
// ============================================================================
function ApiKeysTab() {
  const { message } = App.useApp();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [createForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      setKeys(await getApiKeys());
    } catch {
      message.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async (values: { name: string; role: string; expires_at?: unknown }) => {
    setSaving(true);
    try {
      const expires = values.expires_at
        ? (values.expires_at as { toISOString: () => string }).toISOString()
        : null;
      const result = await createApiKey({
        name: values.name,
        role: values.role as 'admin' | 'readonly',
        expires_at: expires,
      });
      setNewKeyValue(result.api_key.key);
      createForm.resetFields();
      fetchKeys();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Failed to create API key');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    try {
      await revokeApiKey(key.id);
      message.success(`API key "${key.name}" revoked`);
      fetchKeys();
    } catch {
      message.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Key', dataIndex: 'key_prefix', key: 'key_prefix',
      render: (prefix: string) => <code>{prefix}...</code>,
    },
    {
      title: 'Role', dataIndex: 'role', key: 'role',
      render: (role: string) => <Tag color={role === 'admin' ? 'red' : 'blue'}>{role}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'status',
      render: (active: boolean) => active
        ? <Tag color="green">Active</Tag>
        : <Tag color="red">Revoked</Tag>,
    },
    { title: 'Created By', dataIndex: 'created_by_username', key: 'created_by' },
    {
      title: 'Last Used', dataIndex: 'last_used', key: 'last_used',
      render: (v: string | null) => v ? formatTimestamp(v) : 'Never',
    },
    {
      title: 'Expires', dataIndex: 'expires_at', key: 'expires_at',
      render: (v: string | null) => v ? formatDate(v) : 'Never',
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: ApiKey) => record.is_active ? (
        <Popconfirm title={`Revoke "${record.name}"?`} onConfirm={() => handleRevoke(record)}>
          <Button size="small" danger icon={<DeleteOutlined />}>Revoke</Button>
        </Popconfirm>
      ) : null,
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Manage API keys for external integrations. Keys provide programmatic access to the PIM API.
        </Paragraph>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setNewKeyValue(null); setCreateModalOpen(true); }}>
          Create API Key
        </Button>
      </div>

      <Table columns={columns} dataSource={keys} rowKey="id" loading={loading} size="small" />

      <Modal
        title="Create API Key"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); setNewKeyValue(null); createForm.resetFields(); }}
        footer={newKeyValue ? (
          <Button onClick={() => { setCreateModalOpen(false); setNewKeyValue(null); }}>Done</Button>
        ) : null}
      >
        {newKeyValue ? (
          <div>
            <Alert
              type="warning"
              message="Copy this key now — it will not be shown again."
              style={{ marginBottom: 16 }}
            />
            <Input.Group compact>
              <Input
                value={newKeyValue}
                readOnly
                style={{ width: 'calc(100% - 40px)', fontFamily: 'monospace' }}
              />
              <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(newKeyValue)} />
            </Input.Group>
          </div>
        ) : (
          <Form form={createForm} layout="vertical" onFinish={handleCreate}>
            <Form.Item name="name" label="Key Name" rules={[{ required: true, message: 'Give this key a descriptive name' }]}>
              <Input placeholder="e.g., ERP Integration" />
            </Form.Item>
            <Form.Item name="role" label="Permissions" initialValue="readonly">
              <Select options={[
                { value: 'readonly', label: 'Read-only — Search and browse products' },
                { value: 'admin', label: 'Admin — Full access' },
              ]} />
            </Form.Item>
            <Form.Item name="expires_at" label="Expiry (optional)">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving} block>Create Key</Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}

// ============================================================================
// Settings Page
// ============================================================================
export function SettingsPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);

  const [smtpForm] = Form.useForm<SmtpSettings>();
  const [passwordForm] = Form.useForm<PasswordChangeRequest & { confirm_password: string }>();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      setSettings(data);
      smtpForm.setFieldsValue(data.smtp);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSmtpSave = async (values: SmtpSettings) => {
    setSaving(true);
    try {
      const response = await updateSmtpSettings(values);
      if (response.status === 'success') {
        message.success('Email settings saved');
        if (response.settings) {
          setSettings(response.settings);
          smtpForm.setFieldsValue(response.settings.smtp);
        }
      } else {
        message.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save SMTP settings:', error);
      message.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (values: PasswordChangeRequest & { confirm_password: string }) => {
    setSaving(true);
    try {
      const response = await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      if (response.status === 'success') {
        message.success('Password changed successfully');
        passwordForm.resetFields();
      } else {
        message.error(response.error || 'Failed to change password');
      }
    } catch (error: unknown) {
      console.error('Failed to change password:', error);
      const err = error as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading settings...</div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'email',
      label: (
        <Space>
          <MailOutlined />
          Email Notifications
        </Space>
      ),
      children: (
        <Card>
          <Paragraph type="secondary">
            Configure SMTP settings for email notifications when NIQ Brandbank syncs complete or fail.
            Leave disabled if you only want to monitor status through the UI.
          </Paragraph>

          <Form
            form={smtpForm}
            layout="vertical"
            onFinish={handleSmtpSave}
            initialValues={settings?.smtp}
          >
            <Form.Item
              name="enabled"
              label="Enable Email Notifications"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Divider>SMTP Server</Divider>

            <Space size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                name="host"
                label="SMTP Host"
                rules={[{ required: smtpForm.getFieldValue('enabled'), message: 'Required when enabled' }]}
                style={{ minWidth: 250 }}
              >
                <Input placeholder="smtp.example.com" />
              </Form.Item>

              <Form.Item
                name="port"
                label="Port"
                style={{ width: 100 }}
              >
                <InputNumber min={1} max={65535} />
              </Form.Item>

              <Form.Item
                name="use_tls"
                label="Use TLS"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="use_ssl"
                label="Use SSL"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Space>

            <Space size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                name="username"
                label="Username"
                style={{ minWidth: 250 }}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                style={{ minWidth: 250 }}
              >
                <Input.Password placeholder="********" />
              </Form.Item>
            </Space>

            <Form.Item
              name="from_email"
              label="From Address"
              style={{ maxWidth: 400 }}
            >
              <Input placeholder="noreply@example.com" />
            </Form.Item>

            <Divider>Notification Recipients</Divider>

            <Alert
              type="info"
              message="Separate multiple email addresses with commas"
              style={{ marginBottom: 16 }}
            />

            <Space size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                name="success_to_email"
                label="Success Notifications To"
                style={{ minWidth: 300 }}
              >
                <Input placeholder="admin@example.com" />
              </Form.Item>

              <Form.Item
                name="success_cc_email"
                label="Success Notifications CC"
                style={{ minWidth: 300 }}
              >
                <Input placeholder="team@example.com" />
              </Form.Item>
            </Space>

            <Space size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                name="failure_to_email"
                label="Failure Notifications To"
                style={{ minWidth: 300 }}
              >
                <Input placeholder="admin@example.com" />
              </Form.Item>

              <Form.Item
                name="failure_cc_email"
                label="Failure Notifications CC"
                style={{ minWidth: 300 }}
              >
                <Input placeholder="team@example.com" />
              </Form.Item>
            </Space>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  Save Email Settings
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (settings) {
                      smtpForm.setFieldsValue(settings.smtp);
                    }
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: 'providers',
      label: (
        <Space>
          <ApiOutlined />
          Providers
        </Space>
      ),
      children: <ProvidersTab />,
    },
    {
      key: 'users',
      label: (
        <Space>
          <TeamOutlined />
          Users
        </Space>
      ),
      children: <UsersTab />,
    },
    {
      key: 'apikeys',
      label: (
        <Space>
          <KeyOutlined />
          API Keys
        </Space>
      ),
      children: <ApiKeysTab />,
    },
    {
      key: 'account',
      label: (
        <Space>
          <UserOutlined />
          Account
        </Space>
      ),
      children: (
        <Card>
          <Paragraph type="secondary">
            Change your own password. Changes take effect immediately.
          </Paragraph>

          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
            style={{ maxWidth: 400 }}
          >
            <Form.Item
              name="current_password"
              label="Current Password"
              rules={[{ required: true, message: 'Please enter your current password' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter current password"
              />
            </Form.Item>

            <Form.Item
              name="new_password"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter a new password' },
                { min: 8, message: 'Password must be at least 8 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter new password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label="Confirm New Password"
              dependencies={['new_password']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: 'aliases',
      label: (
        <Space>
          <SwapOutlined />
          Name Aliases
        </Space>
      ),
      children: <AliasesTab />,
    },
  ];

  return (
    <div>
      <Title level={3}>Settings</Title>
      <Text type="secondary">
        Configure application settings for email notifications, data providers, user management, and API keys.
      </Text>

      <div style={{ marginTop: 24 }}>
        <Tabs items={tabItems} />
      </div>
    </div>
  );
}
